import { DownloadOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { Inbox, useGetOneInboxQuery } from 'api/InboxApi';
import LogoComponent from 'components/Common/LogoComponent/LogoComponent';
import MessageContentPreview from 'components/Inbox/MessageContentPreview/MessageContentPreview';
import DownloadLink from 'components/Message/DownloadLink/DownloadLink';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { useSelector } from 'react-redux';
import { closeModal, selectModalData, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import dayjs from 'utils/dayjs';
import { getFullNameOrEmail, getMessageTypeText } from 'utils/utils';
import './PreviewInboxModalComponent.css';

export const PREVIEW_INBOX_MODAL_NAME = 'PREVIEW_INBOX_MODAL_NAME';

type AllProps = {
  inboxId: string | null;
  inboxItem?: Inbox;
};

function PreviewInboxModalComponent({ inboxId, inboxItem: initialInboxItem }: AllProps) {
  const dispatch = useAppDispatch();

  const { data: fetchedInboxItem, isLoading } = useGetOneInboxQuery(
    { id: inboxId || '' },
    { skip: !inboxId || Boolean(initialInboxItem) }
  );

  const handleCloseModal = () => {
    dispatch(closeModal());
  };

  const inboxItem = initialInboxItem || fetchedInboxItem;
  const designProps = inboxItem?.designProps;

  return (
    <Modal open={true} onCancel={handleCloseModal} footer={null}>
      <SpinComponent isLoading={isLoading}>
        {inboxItem && (
          <>
            <div className="preview-inbox-modal-component">
              {designProps && (
                <div className="text-center">
                  <LogoComponent companyConfig={designProps} maxHeight={50} />
                  <div className="brand-title">
                    Your message from {getFullNameOrEmail(inboxItem.message.user)} courtesy of{' '}
                    <span style={{ color: designProps.colorPrimary ?? undefined }}>{designProps.companyName}</span>.
                  </div>
                </div>
              )}

              <div className="title">
                {getMessageTypeText(inboxItem.message.type)} - {dayjs(inboxItem.createdAt).format('D/M/YYYY')}
              </div>
              <div className="author">from {getFullNameOrEmail(inboxItem.message.user)}</div>
              <div className="content">
                <MessageContentPreview
                  message={inboxItem.message}
                  messageFile={inboxItem.messageFile}
                  designProps={designProps}
                />
              </div>

              <div className="modal-footer">
                <DownloadLink
                  message={inboxItem.message}
                  messageFile={inboxItem.messageFile}
                  designProps={designProps}
                  text={
                    <Button icon={<DownloadOutlined />} block>
                      Download
                    </Button>
                  }
                />
              </div>
            </div>
          </>
        )}
      </SpinComponent>
    </Modal>
  );
}

function PreviewInboxModalComponentWrapper() {
  const modalName = useSelector(selectModalName);
  const modalData = useSelector(selectModalData);
  const inboxId = modalData?.id || null;
  const inboxItem = modalData?.inboxItem as Inbox | undefined;

  return modalName === PREVIEW_INBOX_MODAL_NAME ? <PreviewInboxModalComponent inboxId={inboxId} inboxItem={inboxItem} /> : null;
}

export default PreviewInboxModalComponentWrapper;
